'use client';

import { useEffect, useState } from 'react';
import { BackendAuth } from '@/lib/auth-backend';
import { toAbsoluteUrl } from '@/lib/helpers';

// Simple in-memory cache so multiple components reuse the same domain config
let cachedDomainConfig = null;
let domainConfigPromise = null;

const FALLBACK_LOGOS = {
  light: toAbsoluteUrl('/media/app/xoffi-logo-white.png'),
  dark: toAbsoluteUrl('/media/app/xoffi-logo-dark.png'),
  small: toAbsoluteUrl('/media/app/xoffi.png'),
};

function normalizeAssetPath(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  if (/^(https?:|data:|blob:)/i.test(trimmed)) {
    return trimmed;
  }

  if (trimmed.startsWith('/api/')) {
    return trimmed;
  }

  const prefixed = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return toAbsoluteUrl(prefixed);
}

async function fetchDomainConfig() {
  if (cachedDomainConfig) {
    return cachedDomainConfig;
  }

  if (!domainConfigPromise) {
    domainConfigPromise = BackendAuth.apiCallJson('/api/domain/config')
      .then((data) => {
        cachedDomainConfig = data || null;
        return cachedDomainConfig;
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        domainConfigPromise = null;
      });
  }

  return domainConfigPromise;
}

function pickLogo(config, candidates, fallback) {
  if (!config) {
    return fallback;
  }

  for (const key of candidates) {
    const urlVariant = normalizeAssetPath(config?.[`${key}_url`]);
    if (urlVariant) {
      return urlVariant;
    }

    const rawVariant = normalizeAssetPath(config?.[key]);
    if (rawVariant) {
      return rawVariant;
    }
  }

  return fallback;
}

export function useDomainConfig() {
  const [config, setConfig] = useState(cachedDomainConfig);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(!cachedDomainConfig);

  useEffect(() => {
    let isActive = true;

    if (cachedDomainConfig) {
      setConfig(cachedDomainConfig);
      setIsLoading(false);
      return () => {
        isActive = false;
      };
    }

    fetchDomainConfig()
      .then((data) => {
        if (!isActive) {
          return;
        }
        setConfig(data);
        setError(null);
      })
      .catch((err) => {
        if (!isActive) {
          return;
        }
        setError(err);
      })
      .finally(() => {
        if (isActive) {
          setIsLoading(false);
        }
      });

    return () => {
      isActive = false;
    };
  }, []);

  return { config, error, isLoading };
}

export function useDomainLogos() {
  const { config } = useDomainConfig();
  const [logos, setLogos] = useState({ ...FALLBACK_LOGOS });

  useEffect(() => {
    if (!config) {
      return;
    }

    setLogos({
      light: pickLogo(config, ['logo_light', 'logo'], FALLBACK_LOGOS.light),
      dark: pickLogo(config, ['logo_dark', 'logo_light'], FALLBACK_LOGOS.dark),
      small: pickLogo(config, ['logo', 'logo_light', 'logo_dark'], FALLBACK_LOGOS.small),
    });
  }, [config]);

  return logos;
}

export { FALLBACK_LOGOS as DOMAIN_LOGO_FALLBACKS };
